import { useCallback, useContext, useEffect, useState } from 'react'

import { AppContext, useHost } from '../../providers/app-provider'
import { useAuthentication } from '../../providers/auth-provider'
import Api from '../api'
import { getFormattedDate, getFormattedTime } from '../helpers'

export const getPausedState = (device: IDeviceAPIObject) => {
  const { pauseStartDate, pauseEndDate } = device || {}

  const now = new Date()
  const startDate = new Date(pauseStartDate)
  const endDate = new Date(pauseEndDate)
  const isPaused = startDate < now && endDate > now
  const willBePaused = startDate > now

  return {
    now,
    startDate,
    endDate,
    isPaused,
    willBePaused,
  }
}

const getTimezoneAbbreviation = (timezone: string) => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      })
        .formatToParts()
        .find((part) => part.type === 'timeZoneName')?.value ?? 'UTC'
    )
  } catch {
    return 'UTC' // Fallback
  }
}

const guessTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

const usePauseDevice = ({ device, timezone }: { device: IDeviceAPIObject; timezone?: string }) => {
  const folderId = device?.folderId
  const { auth, ownUser } = useAuthentication()
  const host = useHost()
  const { pageRefreshCounter } = useContext(AppContext)
  const [folderTimezone, setFolderTimezone] = useState<string | undefined>(timezone)
  const [timezoneAbbr, setTimezoneAbbr] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  let { pauseStartDate, pauseEndDate } = device || {}
  if (!pauseStartDate) pauseStartDate = new Date(0).toISOString()
  if (!pauseEndDate) pauseEndDate = new Date(0).toISOString()

  useEffect(() => {
    const selectedTimezone = folderTimezone || ownUser?.information?.timezone || guessTimezone()
    setTimezoneAbbr(getTimezoneAbbreviation(selectedTimezone))
  }, [folderTimezone, ownUser?.information?.timezone])

  const fetchFolder = useCallback(async () => {
    if (!host || !auth.user?.access_token) return
    if (typeof folderTimezone === 'undefined') {
      const res = await Api.get(`${host}/api/folders/${folderId}`)
      if (res.ok) {
        setFolderTimezone(res.body.timezone)
      }
    }
  }, [auth.user?.access_token, folderId, folderTimezone, host])

  const onRefresh = useCallback(async () => {
    setIsLoading(true)
    await fetchFolder()
    setIsLoading(false)
  }, [fetchFolder])

  useEffect(() => {
    onRefresh()
  }, [onRefresh, pageRefreshCounter])

  const { now, startDate, endDate, isPaused, willBePaused } = getPausedState(device)
  const endsAtMidnight = getFormattedTime(pauseEndDate, 'h:mm aa') === '11:59 PM'
  const startsAtMidnight = getFormattedTime(pauseStartDate, 'h:mm aa') === '12:00 AM'

  let pausedText = ''

  if (startDate && endDate) {
    // Currently paused, only show when pause will end
    if (startDate < now && endDate > now) {
      const formattedEndDate = getFormattedDate(pauseEndDate, "PP 'at' p")
      pausedText = `Paused until ${formattedEndDate} - ${timezoneAbbr}`
    }
    // Will be paused, show start and end of pause
    if (startDate > now && endDate > now) {
      if (endsAtMidnight) {
        const formattedStartDate = (startsAtMidnight ? getFormattedDate(pauseStartDate, 'PP') : getFormattedDate(pauseStartDate, "PP 'at' p")) ?? ''
        const formattedEndDate = getFormattedDate(pauseEndDate, 'PP') ?? ''
        pausedText = `Paused from ${formattedStartDate} through the end of ${formattedEndDate} - ${timezoneAbbr}`
      } else {
        const formattedStartDate = getFormattedDate(pauseStartDate, "PP 'at' p") ?? ''
        const formattedEndDate = getFormattedDate(pauseEndDate, "PP 'at' p") ?? ''
        pausedText = `Paused from ${formattedStartDate} until ${formattedEndDate} - ${timezoneAbbr}`
      }
    }
  }

  return {
    isPaused,
    willBePaused,
    pausedText,
    folderTimezone,
    pauseStartDate,
    pauseEndDate,
    onRefresh,
    isLoading,
  }
}

export default usePauseDevice
